import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { getSiteContent, updateSiteContent } from '../../api/api';
import { FileUpload } from '../../components/FileUpload';

export const AdminContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroPrimaryCTA: '',
    heroSecondaryCTA: '',
    heroMediaUrl: '',
    logoUrl: '',
    siteName: '',
    siteNameColor: '#FF1B6D',
    siteNameGradient: '',
    communityTitle: '',
    communityDescription: '',
    communityCTA: '',
    aboutTitle: '',
    aboutDescription: '',
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',
    footerTagline: '',
    features: [
      { title: '', description: '', icon: 'users' },
      { title: '', description: '', icon: 'calendar' },
      { title: '', description: '', icon: 'image' }
    ],
    sectionHeroVisible: true,
    sectionFeaturesVisible: true,
    sectionEventsVisible: true,
    sectionCommunityVisible: true,
    sectionTeamPreviewVisible: true,
    sectionGalleryPreviewVisible: true,
  });
  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const data = await getSiteContent();
      setFormData(data);
    } catch (error) {
      toast.error("Impossible de charger le contenu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if(e) e.preventDefault();
    setIsSaving(true);
    try {
      await updateSiteContent(formData);
      toast.success("Contenu mis à jour avec succès !");
      await fetchContent();
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index][field] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF1B6D]"></div></div>;
  }

  const sectionToggles = [
    { key: 'sectionHeroVisible', label: 'Section Hero' },
    { key: 'sectionFeaturesVisible', label: 'Section "Pourquoi Nous Choisir"' },
    { key: 'sectionEventsVisible', label: 'Section Evenements' },
    { key: 'sectionCommunityVisible', label: 'Section Communaute' },
    { key: 'sectionTeamPreviewVisible', label: 'Apercu Equipe' },
    { key: 'sectionGalleryPreviewVisible', label: 'Apercu Galerie' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Modifier le Contenu du Site</h2>
        <Button onClick={handleSubmit} disabled={isSaving} className="bg-[#FF1B6D] hover:bg-[#E01660] text-white">
          <Save size={18} className="mr-2" />{isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Visibilite des Sections */}
        <Card>
          <CardHeader><CardTitle>Visibilite des Sections (Page d'accueil)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {sectionToggles.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between py-2 border-b last:border-b-0">
                <Label htmlFor={key} className="text-base cursor-pointer">{label}</Label>
                <Switch
                  id={key}
                  checked={formData[key] !== false}
                  onCheckedChange={(checked) => setFormData({ ...formData, [key]: checked })}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Logo et Nom du site */}
        <Card>
          <CardHeader><CardTitle>Logo & Nom du Site</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <FileUpload
              value={formData.logoUrl || ''}
              onChange={(url) => setFormData({ ...formData, logoUrl: url })}
              accept="image/*"
              label="Logo du site"
              previewSmall
            />
            <p className="text-sm text-gray-500">
              Ce logo s'affiche en haut a gauche du site dans la barre de navigation.
            </p>
            <div>
              <Label htmlFor="siteName">Nom du site (a cote du logo)</Label>
              <Input
                id="siteName"
                value={formData.siteName || ''}
                onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                placeholder="zerounproduction"
              />
            </div>
            <div>
              <Label>Couleur du nom</Label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.siteNameColor || '#FF1B6D'}
                  onChange={(e) => setFormData({ ...formData, siteNameColor: e.target.value, siteNameGradient: '' })}
                  className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                />
                <Input
                  value={formData.siteNameColor || '#FF1B6D'}
                  onChange={(e) => setFormData({ ...formData, siteNameColor: e.target.value, siteNameGradient: '' })}
                  placeholder="#FF1B6D"
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label>Degrade (optionnel, remplace la couleur)</Label>
              <div className="flex gap-3 items-center mt-1">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Couleur 1</p>
                  <input
                    type="color"
                    value={(() => {
                      const match = (formData.siteNameGradient || '').match(/#[0-9A-Fa-f]{6}/g);
                      return match ? match[0] : '#FF1B6D';
                    })()}
                    onChange={(e) => {
                      const match = (formData.siteNameGradient || '').match(/#[0-9A-Fa-f]{6}/g);
                      const color2 = match && match[1] ? match[1] : '#FFD700';
                      setFormData({ ...formData, siteNameGradient: `linear-gradient(90deg, ${e.target.value}, ${color2})` });
                    }}
                    className="w-12 h-12 rounded cursor-pointer border border-gray-300"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Couleur 2</p>
                  <input
                    type="color"
                    value={(() => {
                      const match = (formData.siteNameGradient || '').match(/#[0-9A-Fa-f]{6}/g);
                      return match && match[1] ? match[1] : '#FFD700';
                    })()}
                    onChange={(e) => {
                      const match = (formData.siteNameGradient || '').match(/#[0-9A-Fa-f]{6}/g);
                      const color1 = match ? match[0] : '#FF1B6D';
                      setFormData({ ...formData, siteNameGradient: `linear-gradient(90deg, ${color1}, ${e.target.value})` });
                    }}
                    className="w-12 h-12 rounded cursor-pointer border border-gray-300"
                  />
                </div>
                <div
                  className="w-24 h-12 rounded border border-gray-300"
                  style={{ background: formData.siteNameGradient || 'linear-gradient(90deg, #FF1B6D, #FFD700)' }}
                />
                {formData.siteNameGradient && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, siteNameGradient: '' })}
                    className="text-sm text-red-500 hover:text-red-700 underline"
                  >
                    Supprimer le degrade
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Choisissez 2 couleurs pour creer un degrade. Le degrade remplace la couleur unie.
              </p>
            </div>
            {/* Apercu */}
            <div className="p-4 bg-gray-900 rounded-lg flex items-center gap-3">
              {formData.logoUrl && <img src={formData.logoUrl} alt="Logo" className="w-10 h-10 object-contain" />}
              <span
                className="text-2xl font-black"
                style={formData.siteNameGradient ? {
                  background: formData.siteNameGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                } : { color: formData.siteNameColor || '#FF1B6D' }}
              >
                {formData.siteName || 'zerounproduction'}
              </span>
              <span className="text-sm text-gray-400 ml-auto">Apercu</span>
            </div>
          </CardContent>
        </Card>

        {/* Section Hero */}
        <Card>
          <CardHeader><CardTitle>Section Hero (Page d'accueil)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label htmlFor="heroTitle">Titre principal</Label><Input id="heroTitle" value={formData.heroTitle} onChange={(e) => setFormData({ ...formData, heroTitle: e.target.value })} /></div>
            <div><Label htmlFor="heroSubtitle">Sous-titre</Label><Input id="heroSubtitle" value={formData.heroSubtitle} onChange={(e) => setFormData({ ...formData, heroSubtitle: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label htmlFor="heroPrimaryCTA">Bouton principal</Label><Input id="heroPrimaryCTA" value={formData.heroPrimaryCTA} onChange={(e) => setFormData({ ...formData, heroPrimaryCTA: e.target.value })} /></div>
              <div><Label htmlFor="heroSecondaryCTA">Bouton secondaire</Label><Input id="heroSecondaryCTA" value={formData.heroSecondaryCTA} onChange={(e) => setFormData({ ...formData, heroSecondaryCTA: e.target.value })} /></div>
            </div>
            <FileUpload
              value={formData.heroMediaUrl || ''}
              onChange={(url) => setFormData({ ...formData, heroMediaUrl: url })}
              accept="video/*,image/*"
              label="Media d'arriere-plan (video ou image)"
            />
            <p className="text-sm text-gray-500">
              Uploadez une video (.mp4, .webm) ou une image (.jpg, .png, .webp). Le type est detecte automatiquement.
            </p>
          </CardContent>
        </Card>

        {/* Section Features */}
        <Card>
          <CardHeader><CardTitle>Section "Pourquoi Nous Choisir"</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            {formData.features.map((feature, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <h4 className="font-bold text-gray-900">Feature {index + 1}</h4>
                <div><Label htmlFor={`feature${index}Title`}>Titre</Label><Input id={`feature${index}Title`} value={feature.title} onChange={(e) => updateFeature(index, 'title', e.target.value)} /></div>
                <div><Label htmlFor={`feature${index}Desc`}>Description</Label><Textarea id={`feature${index}Desc`} value={feature.description} onChange={(e) => updateFeature(index, 'description', e.target.value)} rows={3} /></div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Section Communaute */}
        <Card>
          <CardHeader><CardTitle>Section Communaute</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label htmlFor="communityTitle">Titre</Label><Input id="communityTitle" value={formData.communityTitle} onChange={(e) => setFormData({ ...formData, communityTitle: e.target.value })} /></div>
            <div><Label htmlFor="communityDescription">Description</Label><Textarea id="communityDescription" value={formData.communityDescription} onChange={(e) => setFormData({ ...formData, communityDescription: e.target.value })} rows={4} /></div>
            <div><Label htmlFor="communityCTA">Texte du bouton</Label><Input id="communityCTA" value={formData.communityCTA} onChange={(e) => setFormData({ ...formData, communityCTA: e.target.value })} /></div>
          </CardContent>
        </Card>

        {/* Section A Propos */}
        <Card>
          <CardHeader><CardTitle>Page A Propos</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label htmlFor="aboutTitle">Titre</Label><Input id="aboutTitle" value={formData.aboutTitle || ''} onChange={(e) => setFormData({ ...formData, aboutTitle: e.target.value })} /></div>
            <div><Label htmlFor="aboutDescription">Description</Label><Textarea id="aboutDescription" value={formData.aboutDescription || ''} onChange={(e) => setFormData({ ...formData, aboutDescription: e.target.value })} rows={4} /></div>
          </CardContent>
        </Card>

        {/* Informations de Contact */}
        <Card>
          <CardHeader><CardTitle>Page Contact & Footer</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label htmlFor="contactEmail">Email de contact</Label><Input id="contactEmail" type="email" value={formData.contactEmail || ''} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} /></div>
            <div><Label htmlFor="contactPhone">Telephone</Label><Input id="contactPhone" value={formData.contactPhone || ''} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} /></div>
            <div><Label htmlFor="contactAddress">Adresse</Label><Input id="contactAddress" value={formData.contactAddress || ''} onChange={(e) => setFormData({ ...formData, contactAddress: e.target.value })} /></div>
            <div><Label htmlFor="footerTagline">Slogan footer</Label><Input id="footerTagline" value={formData.footerTagline || ''} onChange={(e) => setFormData({ ...formData, footerTagline: e.target.value })} placeholder="Cultures Urbaines – Evenements – Expositions" /></div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={isSaving} className="bg-[#FF1B6D] hover:bg-[#E01660] text-white px-8">
            <Save size={18} className="mr-2" />{isSaving ? 'Enregistrement...' : 'Enregistrer toutes les modifications'}
          </Button>
        </div>
      </form>
    </div>
  );
};
